import React, {createContext, useContext, useEffect, useState} from "react";
import {createTheme, ThemeOptions, ThemeProvider as MuiThemeProvider} from "@mui/material/styles";
import {PaletteMode} from "@mui/material";
import {useTranslation} from "react-i18next";
import "../assets/fonts/fonts.css";
import {color_blue, color_green, color_grey, color_orange, color_red} from "@/agentur/AgenturColors";
import {resolveMuiCoreLocale} from "@/app/locale/muiLocale";

export const fontFamily = [
    '"Helvetica Neue"',
    "Inter",
    "-apple-system",
    "BlinkMacSystemFont",
    "Segoe UI",
    "Roboto",
    "Arial",
    "sans-serif"
].join(",");

export interface AppThemeContextProps {
    mode: PaletteMode;
    toggleColorMode: () => void;
    acquireForceLightMode: () => void;
    releaseForceLightMode: () => void;
}

const AppThemeContext = createContext<AppThemeContextProps | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const {i18n} = useTranslation();
    const [mode1, setMode1] = useState<PaletteMode>("light");
    const [forceLightModeRefs, setForceLightModeRefs] = useState<number>(0);

    useEffect(() => {
        const storedMode = localStorage.getItem("themeMode");
        if (storedMode && (storedMode === "light" || storedMode === "dark")) {
            setMode1(storedMode);
        }
    }, []);

    const acquireForceLightMode = () => {
        setForceLightModeRefs((o) => o + 1);
    };

    const releaseForceLightMode = () => {
        setForceLightModeRefs((o) => o - 1);
    };

    const toggleColorMode = () => {
        setMode1((prevMode) => {
            const newMode = prevMode === "light" ? "dark" : "light";
            localStorage.setItem("themeMode", newMode);
            return newMode;
        });
    };

    const effectiveMode = forceLightModeRefs > 0 ? "light" : mode1;
    const dark = effectiveMode === "dark";

    const mode = dark ? "dark" : "light";

    const options: ThemeOptions = {
        palette: {
            mode,
            text: {
                primary: dark ? color_grey[4] : color_blue[2]
            },
            background: {
                default: dark ? color_blue[1] : color_grey[5]
            },
            error: {
                main: color_red[dark ? 3 : 2],
                light: color_red[4],
                dark: color_red[2],
                contrastText: "#333"
            },
            warning: {
                main: color_orange[1],
                light: color_orange[1],
                dark: color_orange[1],
                contrastText: "#333"
            },
            info: {
                main: color_blue[2],
                light: color_blue[3],
                dark: color_blue[1],
                contrastText: "#333"
            },
            success: {
                main: color_green[2],
                light: color_green[3],
                dark: color_green[1],
                contrastText: "#333"
            },
            primary: {
                main: color_blue[dark ? 3 : 2],
                light: color_blue[dark ? 5 : 3],
                dark: color_blue[dark ? 2 : 1],
                contrastText: dark ? "#333" : "#fff"
            }
        },
        breakpoints: {
            values: {
                xs: 0,
                sm: 600,
                md: 960,
                lg: 1280,
                xl: 1920
            }
        },
        typography: {
            fontFamily,
            allVariants: {
                color: dark ? color_grey[4] : color_blue[2]
            }
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        letterSpacing: "0.05em",
                        "& path, & polygon, & rect": {
                            fill: "currentColor"
                        },
                        color: dark ? color_grey[4] : color_blue[2]
                    },
                    contained: {
                        color: color_grey[5]
                    }
                    // outlined: (props) => {
                    //     const x = 1;
                    //     return {
                    //         borderColor: dark ? color_grey[4] : color_blue[2],
                    //         color: dark ? color_grey[4] : color_blue[2],
                    //         // backgroundColor: "transparent",
                    //         borderWidth: "2px",
                    //         "&:hover": {
                    //             borderColor: props.color === "primary" ? color_blue[2] : color_grey[4],
                    //             backgroundColor: "rgba(255, 190, 84, 0.08)"
                    //         }
                    //     };
                    // }
                }
            },
            MuiDialog: {
                styleOverrides: {
                    root: {
                        "--paper-shadow": undefined
                    },
                    paper: {
                        backgroundColor: dark ? color_blue[1] : color_grey[4],
                        backgroundImage: "unset",
                        border: "1px solid white"
                    }
                }
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        backgroundColor: dark ? color_blue[1] : color_grey[4],
                        borderRadius: 6,
                        borderColor: color_grey[4]
                        // "--paper-shadow": undefined,
                    }
                }
            },
            MuiCardHeader: {
                styleOverrides: {
                    title: {
                        fontWeight: "600",
                        fontSize: "21px",
                        paddingTop: "8px",
                        paddingBottom: "12px",
                        borderBottom: `4px solid ${dark ? color_blue[2] : color_green[2]}`
                    }
                }
            },
            // MuiFormHelperText: {
            //     styleOverrides: {
            //         root: {
            //             overflow: "hidden",
            //             position: "relative",
            //             // width: "100%",
            //             "&>div": {
            //                 display: "inline-block",
            //                 whiteSpace: "nowrap",
            //                 animation: `${scrollLeft} 6s linear infinite`
            //             }
            //         }
            //     }
            // },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        "& input:-webkit-autofill": {
                            WebkitBoxShadow: `0 0 0 100px ${dark ? color_blue[1] : color_grey[5]} inset !important`,
                            backgroundColor: "transparent !important",
                            caretColor: "inherit"
                        },
                        "& input:-webkit-autofill:hover": {
                            backgroundColor: "transparent !important"
                        },
                        "& input:-webkit-autofill:focus": {
                            backgroundColor: "transparent !important"
                        },
                        "& input:-webkit-autofill:active": {
                            backgroundColor: "transparent !important"
                        }
                    }
                }
            }
        }
    };

    const theme = createTheme(options, resolveMuiCoreLocale(i18n.language));

    return (
        <AppThemeContext.Provider value={{ mode, toggleColorMode, acquireForceLightMode, releaseForceLightMode }}>
            <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
        </AppThemeContext.Provider>
    );
};

export const useAppTheme = (): AppThemeContextProps => {
    const context = useContext(AppThemeContext);
    if (!context) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return context;
};

export const useLightMode = () => {
    const { acquireForceLightMode, releaseForceLightMode } = useAppTheme();

    useEffect(() => {
        acquireForceLightMode();

        return () => {
            releaseForceLightMode();
        };
    }, [acquireForceLightMode, releaseForceLightMode]);
};
